"use client";

import type {
  QuickPayOutput,
  QuickPayReceiptContext,
} from "@tillix/api-contract/client-schemas";
import {
  FieldControl,
  FieldError,
  FieldLabel,
  FieldRoot,
} from "@tillix/ui-atoms/field";
import { Input } from "@tillix/ui-atoms/input";
import { observer } from "mobx-react-lite";
import { Controller } from "react-hook-form";
import { useT } from "@/features/i18n/hooks/use-t";
import { FEE_BY_METHOD } from "@/features/pay/state/payment-fees";
import { CardNumberInput } from "@/features/payment-method/components/CardNumberInput";
import { CvvInput } from "@/features/payment-method/components/CvvInput";
import { ExpirationMonthYearPicker } from "@/features/payment-method/components/ExpirationMonthYearPicker";
import { useQuickPayCardForm } from "./hooks/use-quick-pay-card-form";
import QuickPayFormLayout from "./QuickPayFormLayout";

interface QuickPayCardFormProps {
  tenantId: string;
  externalId: string;
  nameOnAccount?: string;
  zipCode?: string;
  amount: number;
  onSuccess: (result: QuickPayOutput, context: QuickPayReceiptContext) => void;
  onCancel: () => void;
}

function QuickPayCardForm({
  tenantId,
  externalId,
  nameOnAccount,
  zipCode,
  amount,
  onSuccess,
  onCancel,
}: QuickPayCardFormProps) {
  const { t } = useT();
  const { t: tInputs } = useT("inputs");
  const {
    form,
    handleFormSubmit,
    rootError,
    paymentStatus,
    isSubmitting,
    isValid,
  } = useQuickPayCardForm({
    tenantId,
    externalId,
    nameOnAccount,
    zipCode,
    amount,
    onSuccess,
  });

  return (
    <QuickPayFormLayout
      amount={amount}
      fee={FEE_BY_METHOD.CARD}
      feeLabel={t("pay.cardProcessingFee")}
      form={form}
      isSubmitting={isSubmitting}
      isValid={isValid}
      keyPrefix="qp-card"
      onCancel={onCancel}
      onSubmit={handleFormSubmit}
      paymentStatus={paymentStatus}
      rootError={rootError}
    >
      <Controller
        control={form.control}
        name="cardHolderName"
        render={({ field, fieldState }) => (
          <FieldRoot name={field.name}>
            <FieldLabel htmlFor="qp-card-holder">
              {t("quickPay.nameOnCard")}
            </FieldLabel>
            <FieldControl
              render={(defaultProps) => (
                <Input
                  {...defaultProps}
                  {...field}
                  autoComplete="name"
                  id="qp-card-holder"
                  placeholder={t("quickPay.nameOnCard")}
                />
              )}
            />
            <FieldError>
              {fieldState.error ? tInputs("validation.invalidName") : undefined}
            </FieldError>
          </FieldRoot>
        )}
      />

      <CardNumberInput id="qp-card-number">
        <ExpirationMonthYearPicker
          monthId="qp-exp-m"
          monthName="expiryMonth"
          yearId="qp-exp-y"
          yearName="expiryYear"
        />
        <CvvInput id="qp-cvv" />
      </CardNumberInput>

      <Controller
        control={form.control}
        name="email"
        render={({ field, fieldState }) => (
          <FieldRoot name={field.name}>
            <FieldLabel htmlFor="qp-email">
              {t("quickPay.emailForReceipt")}
            </FieldLabel>
            <FieldControl
              render={(defaultProps) => (
                <Input
                  {...defaultProps}
                  {...field}
                  autoComplete="email"
                  id="qp-email"
                  placeholder="your@email.com"
                  type="email"
                />
              )}
            />
            <FieldError>
              {fieldState.error
                ? tInputs("validation.invalidEmail")
                : undefined}
            </FieldError>
          </FieldRoot>
        )}
      />
    </QuickPayFormLayout>
  );
}

export default observer(QuickPayCardForm);
